/**
 * Reusable OpenStreetMap view (Leaflet CDN). No Google API key.
 */
import { useEffect, useRef } from "react";
import { Box, Paper } from "@mui/material";
import { addOsmTiles, loadLeaflet, type LeafletMap, type LeafletMarker } from "./osm";

export type OsmPin = {
  lat: number;
  lng: number;
  popup?: string;
  /** Accuracy circle radius in metres (optional). */
  accuracyM?: number | null;
};

export function OsmMap({
  pins,
  height = { xs: 240, sm: 320, md: 400 },
  zoom = 6,
  center = [-6.16, 35.75] as [number, number],
}: {
  pins: OsmPin[];
  height?: object | number | string;
  zoom?: number;
  center?: [number, number];
}) {
  const el = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const pinsRef = useRef<LeafletMarker[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!el.current) return;
      try {
        const L = await loadLeaflet();
        if (cancelled || !el.current) return;
        if (!mapRef.current) {
          mapRef.current = L.map(el.current).setView(center, zoom);
          addOsmTiles(L, mapRef.current);
          // Leaflet needs a size pass after flex layout
          setTimeout(() => mapRef.current?.invalidateSize?.(), 80);
        }
        for (const p of pinsRef.current) p.remove();
        pinsRef.current = [];
        for (const pin of pins) {
          const m = L.marker([pin.lat, pin.lng]).addTo(mapRef.current!);
          if (pin.popup) m.bindPopup(pin.popup);
          pinsRef.current.push(m);
          if (pin.accuracyM && pin.accuracyM > 0 && L.circle) {
            const c = L.circle([pin.lat, pin.lng], {
              radius: pin.accuracyM,
              color: "#15803d",
              fillOpacity: 0.12,
              weight: 1,
            }).addTo(mapRef.current!);
            pinsRef.current.push(c);
          }
        }
        if (pins.length === 1) {
          mapRef.current.setView([pins[0].lat, pins[0].lng], Math.max(zoom, 14));
        } else if (pins.length > 1) {
          const group = L.featureGroup(pinsRef.current);
          mapRef.current.fitBounds(group.getBounds().pad(0.2));
        }
        setTimeout(() => mapRef.current?.invalidateSize?.(), 120);
      } catch {
        /* parent can still show table / coords */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pins, zoom, center]);

  useEffect(() => {
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", height }}>
      <Box ref={el} sx={{ width: "100%", height: "100%", minHeight: 200 }} />
    </Paper>
  );
}
